import type {
  APIBookingDraft,
  APIBookingDraftRoom,
  APIBookingDraftServiceOption,
} from '@/scopes/checkout/infrastructure/api/types';
import type {
  CheckoutPerRoomService,
  CheckoutServiceOption,
  CheckoutServiceRoom,
  ServiceMapper,
  ServiceType,
} from '../../../domain/types';
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper';
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper';
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '../model-mappers/serviceParticipantsMapper';
import { isNil, values } from 'ramda';
import type { AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types';

interface Dependencies {
  serviceName: string;
  serviceTypeMapper: string;
  serviceParticipantsMapper?: ServiceParticipantsMapper;
  serviceOptionMapper?: ServiceOptionMapper;
  serviceBannersMapper?: ServiceBannersMapper;
  serviceType: ServiceType;
}

export const createPerRoomServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutPerRoomService> = (
  dependencies
) => {
  const {
    serviceName,
    serviceType,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies;

  function map(bookingDraft: APIBookingDraft, genericServiceAName?: string) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName || aName === genericServiceAName);

    if (!service) return null;

    const { aName, options } = service;
    const rooms = bookingDraft?.rooms.reduce(
      (acc2: Record<string, CheckoutServiceRoom>, room) => ({
        ...acc2,
        [room.id]: mapRoom(room, options),
      }),
      {}
    );
    const anyRoomHasSelectedValue = values(rooms).some(({ selectedValue }) => selectedValue);

    return {
      id: aName,
      isSelected: anyRoomHasSelectedValue ?? !isNil(anyRoomHasSelectedValue),
      type: serviceType,
      rooms,
      participants: serviceParticipantsMapper.map(service, bookingDraft),
      options: options.reduce(
        (acc2: Record<string, CheckoutServiceOption>, option) => ({
          ...acc2,
          [option.code]: serviceOptionMapper.map(option, service, bookingDraft),
        }),
        {}
      ),
      banners: serviceBannersMapper.map(service),
    };
  }

  function getServiceName() {
    return serviceName;
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper;
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom, options: APIBookingDraftServiceOption[]) {
    const filterRoomOptions = options.filter((option) =>
      option.appliesToParticipants.every((partId) => participantIds.includes(partId))
    );

    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map((participantId) => participantId.toString()),
      selectedValue:
        filterRoomOptions
          .filter(({ isSelected }) => isSelected)
          .find((option) => option.appliesToParticipants.every((partId) => participantIds.includes(partId)))?.code ||
        null,
      availableOptions: filterRoomOptions.map(({ code }) => code),
    };
  }

  function mapSelection(service: CheckoutPerRoomService) {
    const { rooms } = service;
    if (!rooms) return [];

    return Object.values(rooms ?? {})
      .map((room) =>
        room.participantIds.map((participantId) => ({
          participantId,
          value: room.selectedValue ?? null,
        }))
      )
      .flat();
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutPerRoomService,
    previousService: CheckoutPerRoomService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[] = [];
    const ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[] = [];

    if (!service) return { ancillaryAddMapped, ancillaryRemoveMapped };

    const roomsModified = Object.keys(service.rooms).reduce(
      (modified: { id: string; previousValue: string | undefined; newValue: string | undefined }[], id) => {
        const prevRoom = previousService?.rooms[id];
        const newRoom = service.rooms[id];

        const prevSelectedValue = prevRoom?.selectedValue;
        const newSelectedValue = newRoom?.selectedValue;

        if (prevSelectedValue !== newSelectedValue) {
          modified.push({
            id,
            previousValue: prevSelectedValue,
            newValue: newSelectedValue,
          });
        }
        return modified;
      },
      []
    );

    if (roomsModified.length === 1) {
      const room = service.rooms[roomsModified[0].id];

      if (roomsModified[0].newValue) {
        const option = service.options[roomsModified[0].newValue];
        ancillaryAddMapped.push({
          id: roomsModified[0].newValue,
          name: option?.label ?? '',
          price: option?.price ?? 0,
          category: `Ancillary > ${option?.badge ? 'Persuasion' : 'Additional cost'} > ${service.id}`,
          quantity: room.participantIds.length,
          serviceType: service.id,
          extraProperties: {},
        });
      }

      if (roomsModified[0].previousValue) {
        const option = previousService.options[roomsModified[0].previousValue];

        ancillaryRemoveMapped.push({
          id: roomsModified[0].previousValue,
          name: option?.label ?? '',
          price: option?.price ?? 0,
          category: `Ancillary > ${option?.badge ? 'Persuasion' : 'Additional cost'} > ${service.id}`,
          quantity: 1,
          serviceType: service.id,
          extraProperties: {},
        });
      }
    }

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  };
};
